/**
 * ISO-8601 week helpers (runtime-agnostic, date-string based — no timezones).
 * Week format: `YYYY-Www` (e.g. `2026-W29`), days as `YYYY-MM-DD`.
 */

const ISO_WEEK_PATTERN = /^(\d{4})-W(\d{2})$/;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function toDateKey(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Returns true when the value matches `YYYY-Www` with a valid week number. */
export function isIsoWeek(value: string): boolean {
  const match = ISO_WEEK_PATTERN.exec(value);
  if (!match) return false;
  const week = Number.parseInt(match[2], 10);
  return week >= 1 && week <= 53;
}

/**
 * Returns the Monday of the given ISO week as a UTC date.
 * ISO rule: week 1 is the week containing January 4th.
 */
export function isoWeekToMonday(isoWeek: string): Date {
  const match = ISO_WEEK_PATTERN.exec(isoWeek);
  if (!match) throw new Error(`Invalid ISO week: ${isoWeek}`);
  const year = Number.parseInt(match[1], 10);
  const week = Number.parseInt(match[2], 10);

  const jan4 = new Date(Date.UTC(year, 0, 4));
  // getUTCDay(): Sunday=0 … Saturday=6; ISO wants Monday=1 … Sunday=7.
  const jan4IsoDow = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4IsoDow - 1));

  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return monday;
}

/** Returns the seven `YYYY-MM-DD` day keys (Mon–Sun) of the given ISO week. */
export function isoWeekDays(isoWeek: string): string[] {
  const monday = isoWeekToMonday(isoWeek);
  return Array.from({ length: 7 }, (_, offset) => {
    const day = new Date(monday);
    day.setUTCDate(monday.getUTCDate() + offset);
    return toDateKey(day);
  });
}

/** Returns the inclusive `{ start, end }` date range (Mon–Sun) of an ISO week. */
export function isoWeekToDateRange(isoWeek: string): { start: string; end: string } {
  const days = isoWeekDays(isoWeek);
  return { start: days[0], end: days[6] };
}

/** Returns the ISO week (`YYYY-Www`) containing the given `YYYY-MM-DD` date. */
export function dateToIsoWeek(dateKey: string): string {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
  // Shift to the Thursday of the current ISO week — its year is the ISO year.
  const isoDow = date.getUTCDay() === 0 ? 7 : date.getUTCDay();
  const thursday = new Date(date);
  thursday.setUTCDate(date.getUTCDate() + (4 - isoDow));
  const isoYear = thursday.getUTCFullYear();

  const jan1 = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil(((thursday.getTime() - jan1.getTime()) / 86_400_000 + 1) / 7);
  return `${isoYear}-W${pad(week)}`;
}

/** Returns the ISO week that is `offset` weeks after the given one (negative = earlier). */
export function shiftIsoWeek(isoWeek: string, offset: number): string {
  const monday = isoWeekToMonday(isoWeek);
  monday.setUTCDate(monday.getUTCDate() + offset * 7);
  return dateToIsoWeek(toDateKey(monday));
}
